/**
 * Product analytics: the one module that writes funnel events to GTM's
 * `dataLayer`. Everything else calls `track()` (or hands modal events to
 * `trackModalEvent`). See `.claude/plans/analytics-funnel-instrumentation-prd.md`.
 *
 * Rules this module exists to enforce:
 *
 * 1. **No PII reaches GA4.** Google's terms forbid it and the remedy is
 *    deleting the property's data. Params pass an allowlist of keys — an
 *    unknown key is dropped, not forwarded — and any string that looks like an
 *    email or phone number is dropped whatever key it arrives under. User
 *    context carries opaque ids and roles only.
 *
 * 2. **One gate.** Production stage only, never under Playwright or jest. With
 *    `localStorage.analytics_debug = 'true'` every event is also logged to the
 *    console, on any stage, so instrumentation can be checked without GTM
 *    Preview.
 *
 * 3. **Never breaks the app.** Every entry point swallows its own errors and
 *    nothing here is awaited.
 *
 * 4. **Few event names.** GA4 caps distinct names, so a flow or step identity
 *    rides in params (`flow`, `step`), never in the event name.
 *
 * Payload shape: each event is pushed as `{ event, fs: {...} }`, preceded by
 * `{ fs: null }`. GTM merges every push into one persistent data model, so
 * without the reset a `step` from the last wizard would still be attached to
 * the next page view. GTM reads params as Data Layer Variables `fs.<name>`.
 */

import type {
  IModalAnalyticsEvent,
  ModalAnalyticsEventName,
} from '@farmshare/ui-components';
import { environment } from 'environments/environment';
import { isPlaywrightTestMode } from 'lib/test-mode';

declare global {
  interface Window {
    dataLayer: Record<string, unknown>[];
  }
}

export type AnalyticsParamValue = string | number | boolean | undefined | null;
export type AnalyticsParams = Record<string, AnalyticsParamValue>;

/** Who is using the app, as analytics may know it — ids and roles only. */
export interface AnalyticsUserContext {
  user_id?: string;
  vendor_id?: string;
  role?: string;
  /** processor | producer | buyer | both | staff */
  user_type?: string;
  /** Farmshare staff, or an admin impersonating — filter these out of funnels. */
  is_internal?: boolean;
  is_impersonating?: boolean;
}

/** Shared params of the cut sheet questionnaire wizard's events. */
export interface CutsheetEventParams extends AnalyticsParams {
  surface: string;
  questionnaire_id?: string;
  processor_id?: string;
  species?: string;
  question_count?: number;
}

/**
 * The event catalogue. Generic flow/modal events come from the modal
 * renderers (and the cut sheet wizard reuses them); named domain events are
 * added here deliberately, when a funnel needs something generic
 * instrumentation can't produce.
 */
export interface AnalyticsEvents {
  page_view: { page_path: string; page_location: string };
  /**
   * From the cut sheet's Review (or Save) step straight back to a question —
   * the buyer saw their selections and went to fix one.
   */
  cutsheet_review_jump: CutsheetEventParams & {
    question_type: string;
    question_id?: string;
    step_position?: number;
  };
}

export type AnalyticsEventName =
  | keyof AnalyticsEvents
  | ModalAnalyticsEventName;

/**
 * Every param key that may leave the browser. Adding one is a privacy
 * decision: it must never hold names, contact details, free text or prices.
 */
const ALLOWED_PARAMS = new Set<string>([
  // context
  'user_id',
  'vendor_id',
  'role',
  'user_type',
  'is_internal',
  'is_impersonating',
  'shared_device',
  // pages
  'page_path',
  'page_location',
  // flows and steps
  'flow',
  'flow_id',
  'answer_changed',
  'step',
  'step_index',
  'step_position',
  'visible_total',
  'steps_viewed',
  'ms_on_step',
  'ms_total',
  'via',
  'reason',
  'phase',
  'error_type',
  'entry',
  // single-step modals
  'modal',
  'modal_type',
  // cut sheets
  'surface',
  'questionnaire_id',
  'processor_id',
  'question_id',
  'question_type',
  'question_count',
  'species',
]);

/** Context dropped from events raised on a shared device (the kiosk). */
const PERSONAL_CONTEXT_KEYS: (keyof AnalyticsUserContext)[] = [
  'user_id',
  'vendor_id',
  'role',
  'user_type',
  'is_impersonating',
];

/** GA4 truncates param values at 100 characters; do it here, predictably. */
const MAX_VALUE_LENGTH = 100;

const EMAIL_PATTERN = /[^\s@]+@[^\s@]+\.[^\s@]+/;
// Only phone characters, with 7–15 digits among them. Ids are hex and never
// all-digit at that length, so they don't collide with this.
const PHONE_PATTERN = /^\+?[\d\s().-]+$/;

function looksLikeContactDetail(value: string): boolean {
  if (EMAIL_PATTERN.test(value)) return true;
  if (!PHONE_PATTERN.test(value)) return false;
  const digits = value.replace(/\D/g, '').length;
  return digits >= 7 && digits <= 15;
}

let userContext: AnalyticsUserContext = {};
/**
 * The page the user is on, stamped on every event, so a wizard's funnel can be
 * split by where it was opened without each caller saying so.
 */
let currentPagePath: string | undefined;

/**
 * Random id tying one run of a flow's events together — the same helper the
 * modal renderers use, so `flow_id` has one format across both.
 */
export { newFlowId as newAnalyticsFlowId } from '@farmshare/utils';

export function isAnalyticsEnabled(): boolean {
  if (isPlaywrightTestMode()) return false;
  return environment.env === 'production';
}

function isAnalyticsDebug(): boolean {
  try {
    return window.localStorage.getItem('analytics_debug') === 'true';
  } catch {
    return false;
  }
}

/**
 * Applies the allowlist and the contact-detail check. Exported for tests.
 * @param params - Raw params from a caller
 * @returns The params that may be sent, plus the keys that were dropped
 */
export function scrubParams(params: AnalyticsParams): {
  clean: Record<string, string | number | boolean>;
  dropped: string[];
} {
  const clean: Record<string, string | number | boolean> = {};
  const dropped: string[] = [];
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null) continue;
    if (!ALLOWED_PARAMS.has(key)) {
      dropped.push(key);
      continue;
    }
    if (typeof value === 'string') {
      if (looksLikeContactDetail(value)) {
        dropped.push(key);
        continue;
      }
      clean[key] = value.slice(0, MAX_VALUE_LENGTH);
    } else if (typeof value === 'number') {
      if (Number.isFinite(value)) clean[key] = value;
    } else if (typeof value === 'boolean') {
      clean[key] = value;
    } else {
      dropped.push(key);
    }
  }
  return { clean, dropped };
}

interface EmitOptions {
  /**
   * The event comes from a device several people use (the kiosk): drop who is
   * signed in on it, which says nothing about who is actually tapping.
   */
  sharedDevice?: boolean;
}

function emit(
  name: string,
  params: AnalyticsParams,
  { sharedDevice = false }: EmitOptions = {},
): void {
  try {
    const context: AnalyticsParams = {
      ...userContext,
      page_path: currentPagePath,
    };
    if (sharedDevice) {
      for (const key of PERSONAL_CONTEXT_KEYS) delete context[key];
      context.shared_device = true;
    }
    const { clean, dropped } = scrubParams({ ...context, ...params });

    if (isAnalyticsDebug()) {
      // eslint-disable-next-line no-console
      console.info('[analytics]', name, clean);
      if (dropped.length) {
        // eslint-disable-next-line no-console
        console.warn(`[analytics] ${name}: dropped params`, dropped);
      }
      if (typeof clean.step === 'string' && /^step_\d+$/.test(clean.step)) {
        // eslint-disable-next-line no-console
        console.warn(
          `[analytics] ${name}: step "${clean.step}" has no analyticsKey or string title — funnels will break if steps are reordered`,
        );
      }
    }

    if (!isAnalyticsEnabled()) return;
    // GTM drains whatever is already queued when it arrives (it loads after
    // page load), so create the queue rather than waiting for it.
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ fs: null });
    window.dataLayer.push({ event: name, fs: clean });
  } catch {
    // Analytics must never break the app.
  }
}

/**
 * Records a catalogued event.
 * @param name - Event name from the catalogue
 * @param params - Its params; anything off the allowlist is dropped
 * @param options - See EmitOptions
 */
export function track<K extends keyof AnalyticsEvents>(
  name: K,
  params: AnalyticsEvents[K],
  options?: EmitOptions,
): void {
  emit(name, params, options);
}

/**
 * Records one of the generic flow/step events outside a modal — the cut sheet
 * wizard drives the same funnel vocabulary the multi-step modal does.
 * @param name - A flow/step event name
 * @param params - Must include `flow`
 * @param options - See EmitOptions
 */
export function trackFlowEvent(
  name: ModalAnalyticsEventName,
  params: AnalyticsParams & { flow: string },
  options?: EmitOptions,
): void {
  emit(name, params, options);
}

/** `ModalProvider`'s `onAnalyticsEvent` handler. */
export function trackModalEvent(event: IModalAnalyticsEvent): void {
  emit(event.name, event.params);
}

/**
 * Sets the user context stamped on every later event, and pushes the legacy
 * `user` event GTM tags may already trigger on. That push used to carry email,
 * full name and the vendor's business name (often a person's name — "Jane Doe
 * Farms"); it now carries ids and roles only.
 * @param context - The signed-in user, or null when signed out
 */
export function setAnalyticsUser(context: AnalyticsUserContext | null): void {
  try {
    userContext = context ?? {};
    if (!context?.user_id || !isAnalyticsEnabled()) return;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'user',
      userId: context.user_id,
      vendorId: context.vendor_id,
      role: context.role,
      userType: context.user_type,
      isInternal: context.is_internal,
    });
  } catch {
    // Analytics must never break the app.
  }
}

/**
 * Route params whose values are a page's identity rather than a record's —
 * `/processor/calendar` and `/processor/jobs` are different pages, so their
 * `:tab` stays literal. Every other param value is replaced by its name.
 */
const LITERAL_PARAMS = new Set(['tab', 'subTab', '*']);

const OBJECT_ID = /^[0-9a-f]{24}$/i;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const NUMERIC = /^\d+$/;
// Long mixed letter/digit runs: tokens, slugs with ids appended, hashes.
const TOKEN = /^(?=.*\d)(?=.*[a-z])[a-z0-9_-]{16,}$/i;

/**
 * Turns a pathname into a page identity with no record ids in it, so reports
 * group `/processor/customers/66f2…` and `/processor/customers/7a1c…` as one
 * page. Matched route params are swapped for their names; any id-shaped
 * segment left over (module routes resolve below the root router, so their
 * params are invisible here) is swapped by shape.
 * @param pathname - `location.pathname`
 * @param params - Route params from `useParams()`
 * @returns The normalised path, e.g. `/processor/customers/:id`
 */
export function normalizePath(
  pathname: string,
  params: Record<string, string | undefined> = {},
): string {
  const byValue = new Map<string, string>();
  for (const [name, value] of Object.entries(params)) {
    if (!value || LITERAL_PARAMS.has(name)) continue;
    byValue.set(value, `:${name}`);
  }

  const segments = pathname.split('/').map((raw) => {
    if (!raw) return raw;
    let segment = raw;
    try {
      segment = decodeURIComponent(raw);
    } catch {
      // Malformed escape — compare the raw segment.
    }
    const named = byValue.get(segment);
    if (named) return named;
    if (OBJECT_ID.test(segment) || UUID.test(segment)) return ':id';
    if (NUMERIC.test(segment)) return ':n';
    if (TOKEN.test(segment)) return ':token';
    if (looksLikeContactDetail(segment)) return ':redacted';
    return raw;
  });
  return segments.join('/') || '/';
}

/**
 * Records a page view under the normalised path. The query string and hash
 * are never sent — they carry redirect targets, search terms and tokens.
 *
 * This is the only page view GA4 should get. The GTM side has to switch off
 * both of GA4's own: the Google tag's `send_page_view` (on by default; it
 * fires on load with the raw URL, query string and any token in the path) and
 * Enhanced Measurement's history-change page views. Leave either on and every
 * page is counted twice, one of them unscrubbed.
 * @param pathname - `location.pathname`
 * @param params - Route params from `useParams()`
 */
export function trackPageView(
  pathname: string,
  params?: Record<string, string | undefined>,
): void {
  try {
    const pagePath = normalizePath(pathname, params);
    currentPagePath = pagePath;
    track('page_view', {
      page_path: pagePath,
      page_location: `${window.location.origin}${pagePath}`,
    });
  } catch {
    // Analytics must never break the app.
  }
}
